import { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import useCruiseStartBookingFlow from 'hooks/Cruise/useCruiseStartBookingFlow'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import { hasCruiseBundleRates } from 'lib/cruises/cruiseBookingFlow'
import { serialiseOccupancy } from 'lib/search/occupancy'
import type { TripItineraryData } from 'tripPlanner/api/types'
import {
  buildCruiseItineraryActionMessage,
  type CruiseItineraryAction,
} from 'tripPlanner/components/Chat/ChatCruiseAddToTripActionUtils'
import { getCruiseDepartureSelectionInItinerary } from 'tripPlanner/utils/chatItinerary'

const INITIAL_OCCUPANCIES: Array<App.Occupants> = [{
  adults: 2,
  infants: 0,
  children: 0,
  teenagers: 0,
  childrenAge: [],
  childrenBirthDate: [],
}]

interface Options {
  offer: App.CruiseOffer
  itineraryData?: TripItineraryData
  isStreaming?: boolean
  onSendMessage?: (message: string) => Promise<void>
}

function cloneInitialOccupancies(): Array<App.Occupants> {
  return INITIAL_OCCUPANCIES.map((occupancy) => ({
    ...occupancy,
    childrenAge: [...(occupancy.childrenAge ?? [])],
    childrenBirthDate: [...(occupancy.childrenBirthDate ?? [])],
  }))
}

/**
 * Coordinates TripPlanner-specific cruise drawer actions without coupling the
 * shared departure model or pricing primitives to chat or checkout state.
 */
export default function useCruiseOfferDrawer({
  offer,
  itineraryData,
  isStreaming = false,
  onSendMessage,
}: Options) {
  const intl = useIntl()
  const [occupancies, setOccupancies] = useState<Array<App.Occupants>>(cloneInitialOccupancies)
  const [isItineraryActionPending, setIsItineraryActionPending] = useState(false)
  const isItineraryActionPendingRef = useRef(false)
  const { startBookingFlow, startBookingWithOccupancies } = useCruiseStartBookingFlow({
    offer,
    // A selection from this drawer only starts a booking from an empty checkout.
    // The checkout's multi-cruise state represents cabins in one booking, so
    // passing an existing item here could select or replace the wrong cruise.
    cruiseItem: undefined,
  })
  const hasCruiseCheckout = useAppSelector((state) => getCruiseItems(state).length > 0)
  const itineraryDepartureSelection = getCruiseDepartureSelectionInItinerary(offer.id, itineraryData)
  const hasItinerarySelection = !!itineraryDepartureSelection

  useEffect(() => {
    setOccupancies(cloneInitialOccupancies())
    setIsItineraryActionPending(false)
    isItineraryActionPendingRef.current = false
  }, [offer.id])

  const selectDepartureForCheckout = useCallback((departure: App.CruiseDepartureDetails) => {
    if (hasCruiseCheckout) return

    if (hasCruiseBundleRates(offer)) {
      startBookingFlow(departure)
      return
    }

    startBookingWithOccupancies(departure, occupancies, false)
  }, [hasCruiseCheckout, occupancies, offer, startBookingFlow, startBookingWithOccupancies])

  const getItineraryAction = useCallback((departure: App.CruiseDepartureDetails): CruiseItineraryAction => {
    if (!itineraryDepartureSelection) return { type: 'add' }

    if (
      itineraryDepartureSelection.itemId !== undefined &&
      itineraryDepartureSelection.departureId === departure.id
    ) {
      return { type: 'delete', itemId: itineraryDepartureSelection.itemId }
    }

    return { type: 'update', itemId: itineraryDepartureSelection.itemId }
  }, [itineraryDepartureSelection])

  const performDepartureItineraryAction = useCallback((departure: App.CruiseDepartureDetails) => {
    if (isItineraryActionPendingRef.current || isStreaming || !onSendMessage) return

    isItineraryActionPendingRef.current = true
    setIsItineraryActionPending(true)
    const message = buildCruiseItineraryActionMessage({
      cruise: offer,
      departure,
      action: getItineraryAction(departure),
      intl,
      occupancies: serialiseOccupancy(occupancies),
    })

    onSendMessage(message)
      .catch(() => undefined)
      .finally(() => {
        isItineraryActionPendingRef.current = false
        setIsItineraryActionPending(false)
      })
  }, [getItineraryAction, intl, isStreaming, occupancies, offer, onSendMessage])

  return {
    hasItinerarySelection,
    itineraryDepartureId: itineraryDepartureSelection?.departureId,
    itineraryItemId: itineraryDepartureSelection?.itemId,
    performDepartureItineraryAction,
    isItineraryActionDisabled: isItineraryActionPending || isStreaming || !onSendMessage,
    isCheckoutDisabled: hasCruiseCheckout,
    occupancies,
    selectDepartureForCheckout,
    setOccupancies,
  }
}
